import { memo, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, errorText } from "@/lib/api";
import { MONTH_NAMES } from "@/lib/format";
import ConfirmDelete from "@/components/ConfirmDelete";
import CategoryPicker from "@/components/ledger/CategoryPicker";
import PersonalMenu from "@/components/ledger/PersonalMenu";
import { checkRow, outsideFy, cellInput } from "@/components/ledger/rowRules";

const fields = (r) => ({ date: r.date, description: r.description, received: r.received ?? "", spent: r.spent ?? "", category: r.category, notes: r.notes,
  for_whom: r.for_whom || "", what: r.what || "", project: r.project || "" });

function LedgerRow({ row, fy, categories, directors, selected, onSelect, onSaved, onDeleted }) {
  const api = useApi();
  const [draft, setDraft] = useState(() => fields(row));
  const [error, setError] = useState("");
  const last = useRef(fields(row));
  // take the server's values only for cells the user hasn't changed since
  useEffect(() => {
    const next = fields(row);
    const prev = last.current;
    setDraft((d) => Object.fromEntries(Object.keys(next).map((k) => [k, d[k] === prev[k] ? next[k] : d[k]])));
    last.current = next;
  }, [row]);

  const save = async (next = draft) => {
    if (JSON.stringify(next) === JSON.stringify(fields(row))) return setError("");
    const { payload, error: err } = checkRow(next);
    if (err) { setError(err); return toast.error(err); }
    try {
      onSaved(await api("put", `/transactions/${row.id}`, { ...payload, fy: row.fy }));
      setError("");
    } catch (e) { setError(errorText(e)); toast.error(errorText(e)); }
  };
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const personal = (row.flags || []).includes("personal") ? row.personal_director || "" : null;
  const setPersonal = async (director) => {
    try {
      onSaved(await api("post", `/transactions/${row.id}/personal`, { director }));
      const name = directors.find((d) => d.id === director)?.name;
      toast.success(director ? `Moved to ${name}'s account (${row.spent ? "debit" : "credit"})` : "Back to company");
    } catch (e) { toast.error(errorText(e)); }
  };
  const blank = !row.category && personal === null;
  const outside = outsideFy(draft.date, fy);
  const id = row.id;

  return (
    <tr data-testid={`ledger-row-${id}`}
      className={`border-b border-border last:border-0 ${error ? "bg-rose-500/10" : blank ? "bg-amber-500/[0.06]" : ""} hover:bg-accent/30 transition-colors`}>
      <td className="pl-3 w-8"><Checkbox checked={selected} onCheckedChange={() => onSelect(row.id)} data-testid={`ledger-select-${id}`} /></td>
      <td className="w-40">
        <div className="flex items-center">
          <input type="date" className={`${cellInput} font-mono ${outside ? "text-amber-300" : ""}`} value={draft.date || ""}
            onChange={set("date")} onBlur={() => save()} data-testid={`ledger-date-${id}`} />
          {outside && <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" data-testid={`ledger-outside-fy-${id}`}
            title={`Date is outside ${fy.label}`} aria-label={`Date is outside ${fy.label}`} />}
        </div>
      </td>
      <td className="px-2 font-mono text-xs text-muted-foreground" data-testid={`ledger-month-${id}`}>{MONTH_NAMES[row.month - 1]}</td>
      <td className="min-w-[200px] relative">
        <input className={`${cellInput} ${row.bank_account ? "pr-20" : ""}`} value={draft.description} onChange={set("description")} onBlur={() => save()} data-testid={`ledger-description-${id}`} />
        {row.bank_account && (
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] uppercase tracking-wider rounded-full border border-border bg-background px-1.5 py-0.5 text-muted-foreground max-w-[72px] truncate"
            title={`From ${row.bank_account}`} data-testid={`ledger-account-${id}`}>{row.bank_account}</span>
        )}
      </td>
      <td className="w-32 relative">{draft.received !== "" && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-emerald-300/70 pointer-events-none">$</span>}<input className={`${cellInput} font-mono text-right text-emerald-300`} inputMode="decimal" value={draft.received} onChange={set("received")} onBlur={() => save()} data-testid={`ledger-received-${id}`} /></td>
      <td className="w-32 relative">{draft.spent !== "" && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-rose-300/70 pointer-events-none">$</span>}<input className={`${cellInput} font-mono text-right text-rose-300`} inputMode="decimal" value={draft.spent} onChange={set("spent")} onBlur={() => save()} data-testid={`ledger-spent-${id}`} /></td>
      <td className="w-56 px-1">
        <div className="flex items-center gap-1">
          {personal !== null ? (
            <span className="flex-1 px-2 text-xs text-indigo-500" data-testid={`ledger-personal-label-${id}`}>
              Personal {row.spent ? "debit" : "credit"}
            </span>
          ) : (
            <div className="flex-1 min-w-0"><CategoryPicker value={draft.category} categories={categories} testId={`ledger-category-${id}`}
              onChange={(v) => { const next = { ...draft, category: v }; setDraft(next); save(next); }} /></div>
          )}
          <PersonalMenu value={personal} directors={directors} onChange={setPersonal} testId={`ledger-personal-${id}`} />
        </div>
      </td>
      <td className="min-w-[140px]"><input className={cellInput} value={draft.notes} onChange={set("notes")} onBlur={() => save()} data-testid={`ledger-notes-${id}`} /></td>
      {["for_whom", "what", "project"].map((k) => (
        <td key={k} className="min-w-[120px]"><input className={cellInput} value={draft[k]} onChange={set(k)} onBlur={() => save()} data-testid={`ledger-${k.replace("_", "-")}-${id}`} /></td>
      ))}
      <td className="pr-2 text-right">
        {error && <span className="sr-only" data-testid={`ledger-row-error-${id}`}>{error}</span>}
        <ConfirmDelete name={row.description || "this row"} testId={`ledger-delete-${id}`}
          onConfirm={async () => {
            try { await api("delete", `/transactions/${id}`); onDeleted(id); toast.success("Row deleted"); } catch (e) { toast.error(errorText(e)); }
          }} />
      </td>
    </tr>
  );
}

export default memo(LedgerRow);
